import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { allComics } from "@/mocks/comics-detail";

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = allComics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">请检查链接是否正确</p>
            <Link to="/" className="text-accent-500 hover:underline text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = allComics
    .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
    .slice(0, 4);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <nav aria-label="面包屑导航" className="px-6 md:px-10 py-4">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <li><Link to="/" className="hover:text-foreground-900 transition-colors">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to={`/category/${comic.categorySlug}`} className="hover:text-foreground-900 transition-colors">{comic.category}</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><span className="text-foreground-900 font-medium">{comic.title}</span></li>
          </ol>
        </nav>

        <section className="px-6 md:px-10 py-6 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              <div className="w-full md:w-72 flex-shrink-0">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫屋`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{comic.author} 著</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-accent-500 font-semibold">{comic.rating} 分</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{comic.views} 阅读</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-emerald-600 font-medium">{comic.status}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs text-foreground-600 bg-background-100 border border-background-200 px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6 max-w-xl">{comic.description}</p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="px-8 py-3 bg-foreground-950 text-white rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer">
                    从第1话开始阅读
                  </button>
                  <button className="px-8 py-3 border-2 border-foreground-300 text-foreground-700 rounded-full text-sm font-semibold hover:border-foreground-500 transition-all whitespace-nowrap cursor-pointer">
                    收藏漫画
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 md:px-10 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-bold text-foreground-950 mb-6">章节列表</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {comic.chapters.map((ch) => (
                <div
                  key={ch.num}
                  className="flex items-center justify-between px-4 py-3 rounded-xl bg-background-50 hover:bg-background-100 border border-background-200/70 transition-colors cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-800">
                    第{ch.num}话 {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400">{ch.date}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="px-6 md:px-10 py-10 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {relatedComics.map((rc) => (
                  <Link key={rc.id} to={`/comic/${rc.id}`} className="group block">
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        title={`${rc.title} - 韩漫屋推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 truncate">{rc.title}</h3>
                    <p className="text-xs text-foreground-400 mt-0.5">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}